import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Container, Typography, Button, Paper, Box, Alert, CircularProgress } from '@mui/material';
import AuthService from '../services/auth.service';

const EmailConfirmationPage: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Extract token from URL query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tokenParam = queryParams.get('token');
    if (!tokenParam) {
      setError('Invalid or missing confirmation token.');
      setLoading(false);
    } else {
      confirmEmail(tokenParam);
    }
  }, [location]);

  const confirmEmail = async (token: string) => {
    try {
      const response = await AuthService.confirmEmail(token);
      setSuccess(response.message || 'Your email has been confirmed successfully!');
    } catch (err: any) {
      if (err.response && err.response.data) {
        setError(err.response.data.detail || 'Email confirmation failed. Please try again.');
      } else {
        setError('An error occurred. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6">
            Confirming your email...
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          Email Confirmation
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Typography variant="body1" paragraph align="center">
          {success 
            ? 'Thank you for confirming your email. An admin has been notified to approve your account. You will receive an email once your account is approved.'
            : 'There was a problem confirming your email. Please try again or contact support.'}
        </Typography>
        
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/login"
            sx={{ mx: 1 }}
          >
            Go to Login
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default EmailConfirmationPage;
